import { Customer } from '../components/CustomerCard'
import { Task } from '../components/TaskCard'
import { QualityReport } from '../types/quality'
import { navGroups } from '../data/navigation'
import { View } from '../types/view'

export type SearchResultKind = 'customer' | 'task' | 'quality' | 'page'

export interface SearchResult {
  id: string
  kind: SearchResultKind
  /** Group heading shown above the result, e.g. "Kunder". */
  group: string
  title: string
  subtitle?: string
  /** Reference shown on the right, e.g. "K012" or "KS-2026-0002". */
  reference?: string
  view: View
  /** Passed back so the app can select the underlying record. */
  recordId?: string
}

const MAX_PER_GROUP = 4

function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
    .trim()
}

function matches(query: string, ...fields: (string | undefined)[]): boolean {
  const q = normalize(query)
  return fields.some((field) => field && normalize(field).includes(q))
}

interface SearchInput {
  query: string
  customers: Customer[]
  tasks: Task[]
  qualityReports: QualityReport[]
  isAdmin: boolean
}

export function searchEverything({
  query,
  customers,
  tasks,
  qualityReports,
  isAdmin,
}: SearchInput): SearchResult[] {
  if (query.trim().length < 2) return []

  const results: SearchResult[] = []

  customers
    .filter((customer) =>
      matches(
        query,
        customer.name,
        customer.customerId,
        (customer as any).address,
        (customer as any).email,
        (customer as any).phone,
      ),
    )
    .slice(0, MAX_PER_GROUP)
    .forEach((customer) => {
      results.push({
        id: `customer-${customer.id}`,
        kind: 'customer',
        group: 'Kunder',
        title: customer.name,
        subtitle: (customer as any).address || (customer as any).email,
        reference: customer.customerId,
        view: 'customer-detail',
        recordId: customer.id,
      })
    })

  tasks
    .filter((task) =>
      matches(query, task.title, task.taskId, task.customerName),
    )
    .slice(0, MAX_PER_GROUP)
    .forEach((task) => {
      results.push({
        id: `task-${task.id}`,
        kind: 'task',
        group: 'Opgaver',
        title: task.title,
        subtitle: task.customerName,
        reference: task.taskId,
        view: 'task-detail',
        recordId: task.id,
      })
    })

  qualityReports
    .filter((report) =>
      matches(query, report.title, report.reportId, report.customer.name),
    )
    .slice(0, MAX_PER_GROUP)
    .forEach((report) => {
      results.push({
        id: `quality-${report.id}`,
        kind: 'quality',
        group: 'Kvalitetssikring',
        title: report.title,
        subtitle: report.customer.name,
        reference: report.reportId,
        view: 'quality-detail',
        recordId: report.id,
      })
    })

  navGroups
    .flatMap((group) => group.items)
    .filter((item) => !item.adminOnly || isAdmin)
    .filter((item) => matches(query, item.label))
    .slice(0, MAX_PER_GROUP)
    .forEach((item) => {
      results.push({
        id: `page-${item.id}`,
        kind: 'page',
        group: 'Gå til',
        title: item.label,
        view: item.id,
      })
    })

  return results
}

/** Results grouped in display order, preserving the order they were added. */
export function groupResults(
  results: SearchResult[],
): { group: string; items: SearchResult[] }[] {
  const groups: { group: string; items: SearchResult[] }[] = []
  results.forEach((result) => {
    const existing = groups.find((g) => g.group === result.group)
    if (existing) {
      existing.items.push(result)
    } else {
      groups.push({ group: result.group, items: [result] })
    }
  })
  return groups
}
